/**
 * Navigation Configuration
 * 
 * Single source of truth for navigation items across all shells.
 * Prevents drift between BottomNav, TopBar, PublicHeader, and future components.
 * 
 * Related: ADR-0005 Navigation Shell & Menu Architecture
 * Issue #695: Club-first navigation with club-scoped vs static routing
 */

import { Users, Calendar, MessageSquare, Settings, LayoutDashboard, Building2, UserPlus, Flag } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// ===============================
// TYPE DEFINITIONS
// ===============================

export interface MainNavItem {
  id: string;
  path: string; // Keep required (prevents breaking TopBar and other consumers)
  label: string;
  icon: LucideIcon;
  show?: boolean; // Optional conditional visibility
  kind?: 'static' | 'clubScoped'; // Optional: routing behavior hint (Issue #695)
  base?: string; // Optional: documentation for dynamic routing
}

export interface PublicNavItem {
  path: string;
  label: string;
  show?: boolean; // Optional conditional visibility
}

// ===============================
// AUTHENTICATED APP NAVIGATION
// ===============================

/**
 * Main navigation items for authenticated users
 * Used by: BottomNav (mobile), TopBar (desktop)
 * 
 * Issue #695: 4 tabs (Club, Activity, Discuss, Settings) + inert O slot in center
 * Issue #728: "Events" renamed to "Activity" (Events | Groups | Volunteer tabs)
 * Issue #910: "Home" renamed to "Club" with Users icon
 * Navigation layout: Club · Activity | O | Discuss · Settings
 */
export const MAIN_NAV_ITEMS: MainNavItem[] = [
  {
    id: 'home',
    path: '/clubs',
    label: 'Club',
    icon: Users,
    kind: 'clubScoped',
  },
  {
    id: 'activity',
    path: '/activity',
    label: 'Activity',
    icon: Calendar,
    kind: 'clubScoped',
  },
  {
    id: 'discuss',
    path: '/chat',
    label: 'Discuss',
    icon: MessageSquare,
    kind: 'clubScoped',
  },
  {
    id: 'settings',
    path: '/settings',
    label: 'Settings',
    icon: Settings,
    kind: 'static',
  },
];

// ===============================
// PLATFORM ADMIN NAVIGATION
// ===============================

/**
 * Platform Admin navigation items
 * Used by: AdminBottomNav (mobile), AdminTopBar (desktop)
 *
 * Issue #1063: Replace user navigation with admin-specific nav on admin routes
 * Routes: /platform-admin/* and /admin/*
 */
export const ADMIN_NAV_ITEMS: MainNavItem[] = [
  {
    id: 'dashboard',
    path: '/platform-admin/home',
    label: 'Dashboard',
    icon: LayoutDashboard,
    kind: 'static',
  },
  {
    id: 'clubs',
    path: '/platform-admin/clubs',
    label: 'Clubs',
    icon: Building2,
    kind: 'static',
  },
  {
    id: 'invites',
    path: '/admin/invites',
    label: 'Invites',
    icon: UserPlus,
    kind: 'static',
  },
  {
    id: 'reports',
    path: '/admin/reports',
    label: 'Reports',
    icon: Flag,
    kind: 'static',
  },
];

/**
 * Check if a path is an admin route
 */
export function isAdminRoute(pathname: string): boolean {
  return pathname.startsWith('/platform-admin') || pathname.startsWith('/admin');
}

// ===============================
// PUBLIC/MARKETING NAVIGATION
// ===============================

/**
 * Public page navigation items
 * Used by: PublicHeader (desktop nav + mobile hamburger)
 */
export const PUBLIC_NAV_ITEMS: PublicNavItem[] = [
  {
    path: '/',
    label: 'Home',
  },
  {
    path: '/faq',
    label: 'FAQ',
  },
  {
    path: '/about',
    label: 'About',
  },
  {
    path: '/privacy',
    label: 'Privacy',
  },
  {
    path: '/terms',
    label: 'Terms',
  },
  {
    path: '/contact',
    label: 'Contact',
  },
];

// ===============================
// UTILITY FUNCTIONS
// ===============================

/**
 * Filter navigation items based on show property
 * Useful for feature flags or role-based visibility
 */
export function filterVisibleItems<T extends { show?: boolean }>(items: T[]): T[] {
  return items.filter(item => item.show !== false);
}

/**
 * Find navigation item by path
 */
export function findNavItemByPath(path: string): MainNavItem | PublicNavItem | undefined {
  return [...MAIN_NAV_ITEMS, ...PUBLIC_NAV_ITEMS].find(item => item.path === path);
}

/**
 * Get main nav item by ID
 */
export function getMainNavItemById(id: string): MainNavItem | undefined {
  return MAIN_NAV_ITEMS.find(item => item.id === id);
}